import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Inputs } from '../../../../Components';
import { ApplicationLogin } from '../../../../Services/LoginService';
import propxLogin from '../../../../assets/images/pages/login/propx-login.svg';
import { LoginFormsEnum } from './LoginFormsEnum';
import { LOGIN_SUCCESS, LOGIN_FAIL } from '../../../../store/login/Actions';
import { showError } from '../../../../Helper';
import { ArrowLeftIcon } from '../../../../assets/icons';

export const LoginOTPVerificationForm = ({
  setActiveLoginForm,
  loginDto,
  translationPath,
  parentTranslationPath,
}) => {
  const { t } = useTranslation(parentTranslationPath);
  const dispatch = useDispatch();
  const [verificationCodeDigits, setVerificationCodeDigits] = useState({
    digit1: '',
    digit2: '',
    digit3: '',
    digit4: '',
    digit5: '',
    digit6: '',
  });

  // Create refs for each input
  const inputRefs = {
    digit1: useRef(null),
    digit2: useRef(null),
    digit3: useRef(null),
    digit4: useRef(null),
    digit5: useRef(null),
    digit6: useRef(null),
  };

  // Focus on first input when component mounts
  useEffect(() => {
    setTimeout(() => {
      const firstInput = inputRefs.digit1.current;
      if (firstInput && firstInput.querySelector) {
        const input = firstInput.querySelector('input');
        if (input) input.focus();
      }
    }, 300);
  }, []);

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    const newDigits = {};
    digits.forEach((digit, index) => {
      const digitKey = `digit${index + 1}`;
      newDigits[digitKey] = digit;
    });

    setVerificationCodeDigits({
      ...verificationCodeDigits,
      ...newDigits,
    });
  };

    const handleOTPSubmit = async (event) => {
    event.preventDefault();
    
    const code = Object.values(verificationCodeDigits).join('');
    
    if (code.length !== 6) {
      showError(t(`${translationPath}please-enter-complete-otp`));
      return;
    }

    try {
      // Include all login data plus the code and identityVerificationId
      const response = await ApplicationLogin({
        identity: loginDto.identity,
        password: loginDto.password,
        rememberMe: loginDto.rememberMe,
        organizationId: loginDto.organizationId,
        applicationId: loginDto.applicationId,
        identityVerificationId: loginDto.identityVerificationId,
        code: code,
      });
      
      if (response && response.token) {
        dispatch(LOGIN_SUCCESS(response));
      } else {
        dispatch(LOGIN_FAIL());
        showError(t(`${translationPath}invalid-otp`));
      }
    } catch (error) {
      dispatch(LOGIN_FAIL());
      showError(t(`${translationPath}otp-verification-failed`));
    }
  };

  return (
    <div className='login-form-container'>
      <div className='login-header'>
        <img
          src={propxLogin}
          alt='Logo'
        />
      </div>
      <h1 className='heading-1'>{t(`${translationPath}otp-verification`)}</h1>
      <h5 className='heading-2 mb-4'>
        <span>{t(`${translationPath}enter-otp-sent-to-email`)}</span>
      </h5>
      <div className='login-form'>
        <form
          noValidate
          className='form-wrapper'
          onSubmit={handleOTPSubmit}
        >
          <div
            className='otp-container d-flex-v-center-h-between gap-8'
            onPaste={handlePaste}
          >
            {Object.keys(verificationCodeDigits).map((digitKey, index) => {
              const nextDigitKey = index < 5 ? `digit${index + 2}` : null;
              return (
                <Inputs
                  key={digitKey}
                  refs={inputRefs[digitKey]}
                  idRef={`OTP${index + 1}-Ref`}
                  wrapperClasses={`OTP-input ${
                    verificationCodeDigits[digitKey] ? 'filled' : ''
                  }`}
                  inputPlaceholder={t(`${translationPath}zero`)}
                  value={verificationCodeDigits[digitKey]}
                  onInputChanged={(event) => {
                    const newValue = event.target.value;
                    const lastChar = newValue.slice(-1);

                    // Only accept numeric values
                    if (!isNaN(lastChar) && lastChar !== ' ') {
                      setVerificationCodeDigits((prevState) => ({
                        ...prevState,
                        [digitKey]: lastChar,
                      }));

                      // Auto-focus next input when a digit is entered
                      if (
                        lastChar &&
                        nextDigitKey &&
                        inputRefs[nextDigitKey].current
                      ) {
                        setTimeout(() => {
                          const nextInput =
                            inputRefs[nextDigitKey].current.querySelector(
                              'input'
                            );
                          if (nextInput) {
                            nextInput.focus();
                            nextInput.select();
                          }
                        }, 50);
                      }
                    } else if (newValue === '') {
                      // Allow clearing the input
                      setVerificationCodeDigits((prevState) => ({
                        ...prevState,
                        [digitKey]: '',
                      }));
                    }
                  }}
                  onKeyDown={(event) => {
                    // Handle backspace
                    if (
                      event.key === 'Backspace' &&
                      !verificationCodeDigits[digitKey] &&
                      index > 0
                    ) {
                      event.preventDefault();
                      const prevDigitKey = `digit${index}`;
                      const prevInput = inputRefs[prevDigitKey].current;
                      if (prevInput) {
                        const input = prevInput.querySelector('input');
                        if (input) {
                          input.focus();
                          input.select();
                        }
                      }
                    }
                  }}
                  maxLength={1}
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]'
                />
              );
            })}
          </div>

          <div className='d-flex-column-center mt-4'>
            <Button
              className='login-btn'
              type='submit'
            >
              {<span>{t(`${translationPath}verify-and-login`)}</span>}
            </Button>
          </div>

          <div
            className='back-to-login'
            onClick={() => setActiveLoginForm(LoginFormsEnum.SignIn.key)}
          >
            <ArrowLeftIcon
              width={20}
              height={20}
              fill={'#475467'}
              className='back-to-login-icon'
            />
            <span className='back-to-login-text'>
              {t(`${translationPath}back-to-login`)}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};
